/**
 * ZIndex Helper Functions
 * Handles cascading z-index shifts for elements in a spread
 */

/**
 * Compacts zIndex values to remove gaps (e.g., [1, 2, 5] becomes [1, 2, 3])
 * @param {Array} elements - Array of elements with zIndex values
 * @returns {Array} - Elements with compacted zIndex values
 */
export const autoCompactZIndex = (elements) => {
  if (!elements || elements.length === 0) return elements;

  // Sort elements by zIndex
  const sorted = [...elements].sort((a, b) => {
    const zA = typeof a.zIndex === 'number' ? a.zIndex : 0;
    const zB = typeof b.zIndex === 'number' ? b.zIndex : 0;
    return zA - zB;
  });

  // Assign sequential zIndex values
  const compacted = sorted.map((el, index) => ({
    ...el,
    zIndex: index + 1, // Start from 1, not 0
  }));

  return compacted;
};

/**
 * Moves element backward (lower in z-stack) with cascading shift
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to move
 * @returns {Array} - Updated elements array with auto-compacted zIndex
 */
export const moveElementBackward = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return elements;

  const currentZ = typeof selectedElement.zIndex === 'number' ? selectedElement.zIndex : 0;

  // Can't go below 1
  if (currentZ <= 1) return elements;

  const elementBelowZ = currentZ - 1;
  const elementBelow = elements.find((el) => {
    const z = typeof el.zIndex === 'number' ? el.zIndex : 0;
    return z === elementBelowZ;
  });

  // If no element at that zIndex, try to compact first
  if (!elementBelow) {
    return autoCompactZIndex(elements);
  }

  // Cascade shift: move element below up by 1, selected element down by 1
  const updated = elements.map((el) => {
    if (el.id === elementId) {
      return { ...el, zIndex: currentZ - 1 };
    }
    if (el.id === elementBelow.id) {
      return { ...el, zIndex: elementBelowZ + 1 };
    }
    return el;
  });

  // Auto-compact to remove gaps
  return autoCompactZIndex(updated);
};

/**
 * Moves element forward (higher in z-stack) with cascading shift
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to move
 * @returns {Array} - Updated elements array with auto-compacted zIndex
 */
export const moveElementForward = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return elements;

  const currentZ = typeof selectedElement.zIndex === 'number' ? selectedElement.zIndex : 0;

  const elementAboveZ = currentZ + 1;
  const elementAbove = elements.find((el) => {
    const z = typeof el.zIndex === 'number' ? el.zIndex : 0;
    return z === elementAboveZ;
  });

  // If no element at that zIndex, just move to the top
  if (!elementAbove) {
    const maxZ = Math.max(...elements.map((el) => (typeof el.zIndex === 'number' ? el.zIndex : 0)), 0);
    const updated = elements.map((el) =>
      el.id === elementId ? { ...el, zIndex: maxZ + 1 } : el
    );
    // Auto-compact to remove gaps
    return autoCompactZIndex(updated);
  }

  // Cascade shift: move element above down by 1, selected element up by 1
  const updated = elements.map((el) => {
    if (el.id === elementId) {
      return { ...el, zIndex: currentZ + 1 };
    }
    if (el.id === elementAbove.id) {
      return { ...el, zIndex: elementAboveZ - 1 };
    }
    return el;
  });

  // Auto-compact to remove gaps
  return autoCompactZIndex(updated);
};

/**
 * Moves element to the front (highest z-index)
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to move
 * @returns {Array} - Updated elements array with auto-compacted zIndex
 */
export const moveElementToFront = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return elements;

  const maxZ = Math.max(
    ...elements.map((el) => (typeof el.zIndex === 'number' ? el.zIndex : 0)),
    0
  );

  const updated = elements.map((el) =>
    el.id === elementId ? { ...el, zIndex: maxZ + 1 } : el
  );

  // Auto-compact to remove gaps
  return autoCompactZIndex(updated);
};

/**
 * Moves element to the back (lowest z-index)
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to move
 * @returns {Array} - Updated elements array with auto-compacted zIndex
 */
export const moveElementToBack = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return elements;

  // Shift all elements with zIndex >= 1 up by 1
  const updated = elements.map((el) => {
    const z = typeof el.zIndex === 'number' ? el.zIndex : 0;
    if (el.id === elementId) {
      return { ...el, zIndex: 1 };
    }
    if (z >= 1) {
      return { ...el, zIndex: z + 1 };
    }
    return el;
  });

  // Auto-compact to remove gaps
  return autoCompactZIndex(updated);
};

/**
 * Checks if an element is at the top (highest zIndex)
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to check
 * @returns {boolean} - True if element is at top
 */
export const isElementAtTop = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return false;

  const currentZ = typeof selectedElement.zIndex === 'number' ? selectedElement.zIndex : 0;
  const maxZ = Math.max(
    ...elements.map((el) => (typeof el.zIndex === 'number' ? el.zIndex : 0)),
    0
  );

  return currentZ === maxZ;
};

/**
 * Checks if an element is at the bottom (lowest zIndex)
 * @param {Array} elements - Array of elements
 * @param {string} elementId - ID of element to check
 * @returns {boolean} - True if element is at bottom
 */
export const isElementAtBottom = (elements, elementId) => {
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return false;

  const currentZ = typeof selectedElement.zIndex === 'number' ? selectedElement.zIndex : 0;
  return currentZ === 1;
};
